/**
 * Стан застосунку: прогрес, налаштування, щоденна статистика.
 *
 * Усе тримається в памʼяті й пишеться наскрізь в IndexedDB.
 * Жодного сервера — прогрес живе тільки на пристрої (див. D-008).
 */
import { createContext, useContext } from 'react'
import { cards as cardsDb, kv, log } from './db.ts'
import { newCard, review, type Card, type Grade } from './srs.ts'
import { DEFAULT_CONFIG, planSession, type PlannerConfig, type Session } from './planner.ts'
import { dayKey, fromDayKey, daysBetween } from './time.ts'
import type { CourseIndex } from './course.ts'

export interface Settings {
  /** Дата, до якої треба закрити курс. */
  deadline: string
  /** Показувати транскрипцію: авто (перші два тижні), завжди або ніколи. */
  transcription: 'auto' | 'always' | 'never'
  /** Час щоденного нагадування, `HH:MM`, або null. */
  reminder: string | null
  /** Цільова тривалість заняття у хвилинах. */
  dailyMinutes: number
  /** Перший день користування — від нього рахується «авто» для транскрипції. */
  startedAt: string
  /** Стеля повторень за одну сесію. */
  maxReviews: number
}

export interface DayStats {
  introduced: number
  reviewed: number
  seconds: number
}

export interface AppState {
  idx: CourseIndex
  cards: Map<string, Card>
  settings: Settings
  daily: Record<string, DayStats>
  session: Session
  /** Скільки днів поспіль були заняття. */
  streak: number
}

export const DEFAULT_SETTINGS: Settings = {
  deadline: '2026-11-09',
  transcription: 'auto',
  reminder: null,
  dailyMinutes: 35,
  startedAt: dayKey(Date.now()),
  maxReviews: DEFAULT_CONFIG.maxReviewsPerSession,
}

export function plannerConfig(settings: Settings): PlannerConfig {
  return {
    ...DEFAULT_CONFIG,
    deadline: settings.deadline ? fromDayKey(settings.deadline) : null,
    maxReviewsPerSession: settings.maxReviews,
  }
}

/** Чи показувати транскрипцію просто зараз. */
export function showTranscription(settings: Settings, now: number): boolean {
  if (settings.transcription === 'always') return true
  if (settings.transcription === 'never') return false
  // «Авто»: перші два тижні — так, далі транскрипція ховається,
  // щоб очі звикали до польського написання (див. відповідь 5.5).
  return daysBetween(fromDayKey(settings.startedAt), now) < 14
}

/** Рахує серію: скільки днів поспіль до сьогодні були заняття. */
export function computeStreak(daily: Record<string, DayStats>, now: number): number {
  let streak = 0
  for (let back = 0; back < 400; back++) {
    const key = dayKey(now - back * 86_400_000)
    const day = daily[key]
    const active = day && day.introduced + day.reviewed > 0
    if (active) streak++
    else if (back > 0) break
    // Сьогоднішній день ще може бути порожнім — це не рве серію.
  }
  return streak
}

// ── Завантаження і збереження ─────────────────────────────────────────────

export async function loadState(idx: CourseIndex): Promise<Omit<AppState, 'session' | 'streak'>> {
  const [stored, settingsRaw, dailyRaw] = await Promise.all([
    cardsDb.all<Card>().catch(() => [] as Card[]),
    kv.get<Settings>('settings').catch(() => undefined),
    kv.get<Record<string, DayStats>>('daily').catch(() => undefined),
  ])

  const settings: Settings = { ...DEFAULT_SETTINGS, ...(settingsRaw ?? {}) }
  if (!settingsRaw) await kv.set('settings', settings).catch(() => undefined)

  const map = new Map<string, Card>()
  for (const c of stored) if (idx.phraseById.has(c.id)) map.set(c.id, c)

  return { idx, cards: map, settings, daily: dailyRaw ?? {} }
}

export function buildSession(
  idx: CourseIndex,
  cards: Map<string, Card>,
  settings: Settings,
  daily: Record<string, DayStats>,
  now: number,
): Session {
  const all = idx.order.map((id) => cards.get(id) ?? newCard(id))
  return planSession(
    {
      now,
      cards: all,
      order: idx.order,
      introducedToday: daily[dayKey(now)]?.introduced ?? 0,
    },
    plannerConfig(settings),
  )
}

/** Застосовує оцінку до картки і повертає оновлену. */
export function applyGrade(
  card: Card | undefined,
  id: string,
  grade: Grade,
  settings: Settings,
  now: number,
): Card {
  const base = card ?? newCard(id)
  return review(base, grade, {
    now,
    deadline: settings.deadline ? fromDayKey(settings.deadline) : null,
  })
}

export async function persistCard(card: Card) {
  await cardsDb.put(card).catch(() => undefined)
}

export async function persistDaily(daily: Record<string, DayStats>) {
  await kv.set('daily', daily).catch(() => undefined)
}

export async function persistSettings(settings: Settings) {
  await kv.set('settings', settings).catch(() => undefined)
}

export async function logReview(phraseId: string, grade: Grade, ms: number, now: number) {
  await log.add({ at: now, phraseId, grade, ms })
}

// ── Експорт та імпорт прогресу ────────────────────────────────────────────

export interface Backup {
  version: 1
  exportedAt: string
  settings: Settings
  daily: Record<string, DayStats>
  cards: Card[]
}

export function makeBackup(state: Pick<AppState, 'cards' | 'settings' | 'daily'>, now: number): Backup {
  return {
    version: 1,
    exportedAt: new Date(now).toISOString(),
    settings: state.settings,
    daily: state.daily,
    cards: [...state.cards.values()],
  }
}

export async function restoreBackup(backup: Backup): Promise<void> {
  if (backup.version !== 1) throw new Error('Невідома версія файлу резервної копії')
  await cardsDb.clear()
  await cardsDb.putMany(backup.cards)
  await kv.set('settings', backup.settings)
  await kv.set('daily', backup.daily)
}

// ── Контекст ──────────────────────────────────────────────────────────────

export interface Store extends AppState {
  now: number
  grade: (phraseId: string, grade: Grade, ms: number) => void
  update: (patch: Partial<Settings>) => void
  refresh: () => void
  reset: () => Promise<void>
}

export const StoreContext = createContext<Store | null>(null)

export function useStore(): Store {
  const ctx = useContext(StoreContext)
  if (!ctx) throw new Error('useStore викликано поза StoreProvider')
  return ctx
}
